(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else {
    root.DodoRoutineEngine = factory();
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var SKIN = [
    ['cleanser', 'Cleanse', 'AM/PM'],
    ['serum', 'Serum (optional)', 'Per product instructions'],
    ['moisturizer', 'Moisturize', 'AM/PM'],
    ['spf', 'Sun protection', 'AM']
  ];
  var HAIR = [
    ['wash', 'Wash', 'Wash day'],
    ['condition', 'Condition', 'Wash day'],
    ['hair-oil', 'Finish', 'Per product instructions']
  ];

  function cents(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function copyProduct(product) {
    return Object.assign({}, product, {
      goals: product.goals.slice(),
      skinTypes: product.skinTypes.slice()
    });
  }

  function oilRank(product) {
    var name = (product.handle + ' ' + product.title).toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (name.indexOf('langhaarmadchen') !== -1) return 0;
    if (name.indexOf('balea') !== -1) return 1;
    return 2;
  }

  function key(product) {
    return JSON.stringify([
      product.handle, product.id, product.variantId, product.title,
      product.url, product.image, product.price, product.kind,
      product.goals.slice().sort(), product.skinTypes.slice().sort(),
      product.fragranceFree, product.active, product.usage, product.reason
    ]);
  }

  function compare(a, b) {
    var preference = a.kind === 'hair-oil' ? oilRank(a) - oilRank(b) : 0;
    // Prefer basic, non-active products, then lower prices; never depend on input order.
    return preference || Number(a.active) - Number(b.active) || a.price - b.price ||
      (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0);
  }

  function suitable(product, kind, profile) {
    if (!product || product.kind !== kind || product.available !== true ||
        !cents(product.price) || typeof product.active !== 'boolean' ||
        !Array.isArray(product.goals) || !Array.isArray(product.skinTypes)) return false;
    if (profile.fragranceFree === true && product.fragranceFree !== true) return false;
    if (product.goals.length > 0 && product.goals.indexOf(profile.goal) === -1) return false;
    if (kind === 'serum' || kind === 'hair-oil') {
      var goals = kind === 'serum' ? ['hydration', 'shine', 'blemishes'] :
        ['shine', 'dry-ends', 'frizz'];
      if (goals.indexOf(profile.goal) === -1 || product.goals.indexOf(profile.goal) === -1) {
        return false;
      }
    }
    // Empty skinTypes means universal. "unsure" never guesses a specific skin type.
    return kind === 'hair-oil' || product.skinTypes.length === 0 ||
      product.skinTypes.indexOf(profile.skinType) !== -1;
  }

  /**
   * Pure catalog-based selection; prices and budget are nonnegative safe integer cents.
   * Catalog kind/active metadata is authoritative, including salicylic-acid products.
   * Alternatives are individual swaps against the returned routine, not a bundle.
   * Rebuild after a swap before offering another set of alternatives.
   */
  function buildRoutine(profile, catalog) {
    profile = profile || {};
    catalog = Array.isArray(catalog) ? catalog : [];
    var notices = [];
    if (profile.area !== 'skin' && profile.area !== 'hair') {
      return { steps: [], total: 0, notices: ['Choose skin or hair to build a routine.'] };
    }
    var budget = cents(profile.budget) ? profile.budget : 0;
    if (!cents(profile.budget)) notices.push('Please check your budget. We are using a budget of zero for now.');
    var owned = Array.isArray(profile.owned) ? profile.owned : [];
    var steps = (profile.area === 'skin' ? SKIN : HAIR).map(function (definition) {
      return {
        kind: definition[0], label: definition[1], period: definition[2],
        product: null, alternatives: [], owned: owned.indexOf(definition[0]) !== -1,
        message: ''
      };
    });
    var review = profile.sensitive === true || profile.restricted === true;
    if (review) {
      notices.push('Automatic product recommendations are paused. Consult a qualified professional or contact us for a review before choosing products.');
      steps.forEach(function (step) {
        step.message = 'Generic step only; product selection needs review.';
      });
      return { steps: steps, total: 0, notices: notices };
    }

    // Owned kinds have no ingredient metadata: reserve the active allowance.
    var ownedUnknown = steps.some(function (step) { return step.owned; });
    if (ownedUnknown) {
      notices.push('Owned products are excluded from purchases. Their active ingredients are unknown, so no additional active products are suggested.');
    }
    var total = 0;
    var activeCount = ownedUnknown ? 1 : 0;
    var candidates = {};
    var order = profile.area === 'skin' ? [0, 2, 3, 1] : [0, 1, 2];
    order.forEach(function (index) {
      var step = steps[index];
      if (step.owned) {
        step.message = 'Already owned; no repurchase included.';
        return;
      }
      if (step.kind === 'wash' || step.kind === 'condition') {
        step.message = step.kind === 'wash' ?
          'Use a shampoo suited to your hair. We do not currently have a matching product to suggest.' :
          'Use a conditioner suited to your hair. We do not currently have a matching product to suggest.';
        return;
      }
      var matches = catalog.filter(function (product) {
        return suitable(product, step.kind, profile);
      }).sort(compare);
      candidates[step.kind] = matches;
      var allowed = matches.filter(function (product) {
        return !product.active || activeCount === 0;
      });
      var pick = allowed.find(function (product) { return product.price <= budget - total; });
      if (pick) {
        step.product = copyProduct(pick);
        total += pick.price;
        if (pick.active) activeCount += 1;
        step.message = pick.reason || 'Matches your preferences and budget.';
      } else if (!matches.length) {
        step.message = 'We do not currently have an available product matching your preferences for this step.';
      } else if (!allowed.length) {
        step.message = 'No product added: avoid combining active products; review the products already in your routine.';
      } else {
        step.message = 'No matching product fits the remaining budget; leave this step unfilled.';
      }
    });

    steps.forEach(function (step) {
      if (!candidates[step.kind]) return;
      var current = step.product;
      var remaining = budget - total + (current ? current.price : 0);
      var otherActives = activeCount - (current && current.active ? 1 : 0);
      step.alternatives = candidates[step.kind].filter(function (product) {
        return (!current || key(product) !== key(current)) &&
          product.price <= remaining && (!product.active || otherActives === 0);
      }).map(copyProduct);
    });
    if (steps.some(function (step) { return !step.owned && !step.product; })) {
      notices.push('Some steps have no product selected. Only selected products are included in the total.');
    }
    return { steps: steps, total: total, notices: notices };
  }

  return { buildRoutine: buildRoutine };
});
